import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  aircraftProjectRows,
  DAY_KIND_LABEL,
  daysSummary,
  DEFAULT_RANGE_PRESET,
  filterLines,
  kmPerDay,
  kmText,
  lineKmChangeText,
  opsSummary,
  presetRange,
  RANGE_PRESET_LABEL,
  rangeIssue,
  STANDBY_LABEL,
  type OpsFilter,
  type OpsProgress,
  type OpsRange,
  type RangePreset,
  type StandbyKind,
  utilisationTotal,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { Tabs } from '../components/Tabs.tsx';
import {
  loadOpsAircraft,
  loadOpsProgress,
  loadOpsProjects,
  loadOpsRange,
  type OpsAircraft,
  type OpsProject,
  type OpsRangeRows,
} from './api.ts';
import {
  AIRCRAFT_COLUMN_HINT,
  SURVEY_DAYS_TILE_HINT,
  dateText,
  daysText,
  hoursText,
  kmPerHourText,
  litresText,
  localToday,
  utilisationText,
} from './format.ts';
import { KmPerDayChart } from './KmPerDayChart.tsx';
import { HubView } from './HubView.tsx';
import type { HubCrewLoader } from './hubApi.ts';
import { TableWrap } from '../components/TableWrap.tsx';

export interface OpsScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  /** The viewer's local date; tests pass a fixed one. */
  readonly today?: string;
  /** Now, for the trackers' ages; tests pass a fixed one. */
  readonly nowMs?: () => number;
  /** The tab it opens on: the map (default) or the figures. */
  readonly initialTab?: OpsTab;
  /** The staff register's project crew (decision 422), once `p4/staff-register` lands. */
  readonly loadCrew?: HubCrewLoader;
}

export type OpsTab = 'map' | 'figures';
const TABS: ReadonlyArray<readonly [OpsTab, string]> = [
  ['map', 'Map'],
  ['figures', 'Figures'],
];

/**
 * The Operations Hub (docs/specs/portal/operations-hub.md; was Operations oversight): the map of every project with
 * its project list and tracking assets (§14 to §19), and the figures of a date range (§4 to §7).
 */
export function OpsScreen({
  sb,
  tenantId,
  tenantName,
  today = localToday(),
  nowMs,
  initialTab = 'map',
  loadCrew,
}: OpsScreenProps): ReactElement {
  const [tab, setTab] = useState<OpsTab>(initialTab);
  return (
    <section className="page ops">
      <PageHeader
        title="Operations Hub"
        subtitle={`Every project of ${tenantName}: where it is, how far it has come, and the figures behind it.`}
      />
      <Tabs label="Operations Hub sections" tabs={TABS} current={tab} onChange={setTab} idPrefix="ops" />
      <div role="tabpanel" id={`ops-panel-${tab}`} aria-labelledby={`ops-tab-${tab}`} className="stack">
        {tab === 'map' ? (
          <HubView
            sb={sb}
            tenantId={tenantId}
            today={today}
            {...(nowMs ? { nowMs } : {})}
            {...(loadCrew ? { loadCrew } : {})}
          />
        ) : (
          <OpsFigures sb={sb} tenantId={tenantId} today={today} />
        )}
      </div>
    </section>
  );
}

type PresetChoice = RangePreset | 'custom';
const PRESETS: readonly PresetChoice[] = ['last7', 'last30', 'this_week', 'last_week', 'custom'];
const ALL = '';
const STANDBY_ORDER: readonly StandbyKind[] = ['weather', 'diurnal', 'pilot_rdo', 'other'];

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** The Figures tab (spec §4 to §7): every project of the tenant for a date range. */
function OpsFigures({
  sb,
  tenantId,
  today,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly today: string;
}): ReactElement {
  const [preset, setPreset] = useState<PresetChoice>(DEFAULT_RANGE_PRESET);
  const initial = presetRange(DEFAULT_RANGE_PRESET, today);
  const [custom, setCustom] = useState<OpsRange>(initial);
  const [projectId, setProjectId] = useState(ALL);
  const [aircraftId, setAircraftId] = useState(ALL);

  const range: OpsRange = preset === 'custom' ? custom : presetRange(preset, today);
  const issue = rangeIssue(range);

  const [refs, setRefs] = useState<{ projects: OpsProject[]; aircraft: OpsAircraft[] } | null>(null);
  const [refsError, setRefsError] = useState<string | null>(null);
  const loadRefs = useCallback(() => {
    setRefsError(null);
    Promise.all([loadOpsProjects(sb, tenantId), loadOpsAircraft(sb, tenantId)])
      .then(([projects, aircraft]) => setRefs({ projects, aircraft }))
      .catch((e: unknown) => setRefsError(errorText(e)));
  }, [sb, tenantId]);
  useEffect(loadRefs, [loadRefs]);

  // The range's rows. A new range keeps the previous figures on screen, dimmed, until the new ones arrive.
  const [rows, setRows] = useState<{ key: string; data: OpsRangeRows } | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const rangeKey = `${range.from}|${range.to}`;
  useEffect(() => {
    if (issue) return;
    let live = true;
    setRowsError(null);
    const [from, to] = rangeKey.split('|') as [string, string];
    loadOpsRange(sb, tenantId, { from, to })
      .then((data) => live && setRows({ key: rangeKey, data }))
      .catch((e: unknown) => live && setRowsError(errorText(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId, rangeKey, issue, attempt]);

  const filter: OpsFilter = {
    ...range,
    projectId: projectId === ALL ? null : projectId,
    aircraftId: aircraftId === ALL ? null : aircraftId,
  };
  const data = rows?.data ?? null;
  const stale = rows !== null && rows.key !== rangeKey;

  // Progress is project to date: the planned and active projects, and any project flown in the range.
  const progressProjects = useMemo(() => {
    if (!refs) return [];
    const flown = new Set((data?.lines ?? []).map((l) => l.project_id));
    return refs.projects.filter(
      (p) =>
        (projectId === ALL || p.id === projectId) &&
        (p.status === 'planned' || p.status === 'active' || flown.has(p.id)),
    );
  }, [refs, data, projectId]);
  const [progress, setProgress] = useState<Record<string, OpsProgress | string>>({});
  useEffect(() => {
    let live = true;
    for (const p of progressProjects) {
      if (progress[p.id] !== undefined) continue;
      loadOpsProgress(sb, p)
        .then((r) => live && setProgress((m) => ({ ...m, [p.id]: r })))
        .catch((e: unknown) => live && setProgress((m) => ({ ...m, [p.id]: errorText(e) })));
    }
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loaded once per project; `progress` only caches
  }, [sb, progressProjects]);

  const projectName = (id: string): string => {
    const p = refs?.projects.find((x) => x.id === id);
    return p ? `${p.number} ${p.title}` : 'Unknown project';
  };
  const aircraftName = (key: string | null, rego: string | null = null): string => {
    if (key === null || key === 'none') return rego ?? 'Not recorded';
    if (key.startsWith('rego:')) return key.slice(5);
    return refs?.aircraft.find((a) => a.id === key)?.registration ?? 'Unknown aircraft';
  };

  const rangeText = `${dateText(range.from)} to ${dateText(range.to)}`;

  return (
    <div className="stack ops__figures">
      <p className="caption muted">From the line review and daily entries Teslyn Field syncs.</p>
      <div className="ops__filters" role="group" aria-label="Filters">
        <SelectField
          label="Range"
          value={preset}
          onChange={(v) => {
            if (v === 'custom' && preset !== 'custom') setCustom(range);
            setPreset(v);
          }}
          options={PRESETS.map((p) => [p, RANGE_PRESET_LABEL[p]] as const)}
        />
        {preset === 'custom' && (
          <>
            <TextField
              label="From"
              type="date"
              value={custom.from}
              onChange={(v) => setCustom({ ...custom, from: v })}
            />
            <TextField
              label="To"
              type="date"
              value={custom.to}
              onChange={(v) => setCustom({ ...custom, to: v })}
            />
          </>
        )}
        <SelectField
          label="Project"
          value={projectId}
          onChange={setProjectId}
          options={[
            [ALL, 'All projects'] as const,
            ...(refs?.projects ?? []).map((p) => [p.id, `${p.number} ${p.title}`] as const),
          ]}
        />
        <SelectField
          label="Aircraft"
          value={aircraftId}
          onChange={setAircraftId}
          options={[
            [ALL, 'All aircraft'] as const,
            ...(refs?.aircraft ?? []).map((a) => [a.id, a.registration] as const),
          ]}
        />
      </div>
      <p className="caption muted ops__range">{rangeText}</p>

      {issue && (
        <p className="notice notice--fail body" role="alert">
          {issue}
        </p>
      )}
      {refsError && <ErrorState message={refsError} onRetry={loadRefs} />}
      {rowsError && <ErrorState message={rowsError} onRetry={() => setAttempt((n) => n + 1)} />}
      {!issue && !rowsError && !data && <Loading what="operations figures" />}

      {!issue && data && (
        <div className={stale ? 'ops__body ops__body--stale' : 'ops__body'} aria-busy={stale || undefined}>
          <Figures
            data={data}
            filter={filter}
            rangeText={rangeText}
            projectName={projectName}
            aircraftName={aircraftName}
          />
        </div>
      )}

      <section className="stack" aria-labelledby="ops-progress">
        <h2 id="ops-progress" className="heading section-title">
          Progress to date
        </h2>
        <p className="caption muted">
          Project to date, whatever the range: the figures of the Progress page and the Job Summary.
        </p>
        {refs && progressProjects.length === 0 && <p className="body">No planned or active projects.</p>}
        {progressProjects.length > 0 && (
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Project</th>
                  <th className="label">Client</th>
                  <th className="label num">Planned</th>
                  <th className="label num">Accepted</th>
                  <th className="label num">Remaining</th>
                  <th className="label">Complete</th>
                  <th className="label num">Contract</th>
                  <th className="label num">Of contract</th>
                </tr>
              </thead>
              <tbody>
                {progressProjects.map((p) => {
                  const r = progress[p.id];
                  if (r === undefined || typeof r === 'string') {
                    return (
                      <tr key={p.id}>
                        <th scope="row" className="data">
                          {p.number} {p.title}
                        </th>
                        <td colSpan={7} className="caption muted">
                          {r === undefined ? 'Loading progress…' : `Could not load: ${r}`}
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={p.id}>
                      <th scope="row" className="data">
                        {p.number} {p.title}
                      </th>
                      <td>{p.clientName || 'No client'}</td>
                      <td className="num data">{kmText(r.plannedKm)}</td>
                      <td className="num data">{kmText(r.acceptedKm)}</td>
                      <td className="num data">{kmText(r.remainingKm)}</td>
                      <td>
                        <span className="data">{r.percentComplete}%</span>
                        <span className="ops__bar" aria-hidden="true">
                          <span style={{ width: `${Math.min(100, r.percentComplete)}%` }} />
                        </span>
                      </td>
                      <td className="num data">
                        {r.contractKm === null ? (
                          <span className="caption muted">No signed contract</span>
                        ) : (
                          <>
                            {`${Math.round(r.contractKm).toLocaleString('en-AU')} km`}
                            <span className="caption muted ops__sub">
                              {contractNote(
                                r.contractSignedKm ?? r.contractKm,
                                r.contractVariations,
                                progressProjects.find((x) => x.id === p.id)?.contractVariations ?? [],
                              )}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="num data">
                        {r.percentOfContract === null ? '–' : `${r.percentOfContract}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </section>
    </div>
  );
}

/** Under the contract km: "Signed contract", or the signed km and each counted variation's change (decision 258). */
function contractNote(
  signedKm: number,
  counted: readonly number[],
  variations: readonly { readonly number: number; readonly line_km_change: number | null }[],
): string {
  if (counted.length === 0) return 'Signed contract';
  const parts = counted.map((n) => {
    const v = variations.find((x) => x.number === n);
    return `variation ${n} ${lineKmChangeText(v?.line_km_change ?? 0)}`;
  });
  return `Signed ${Math.round(signedKm).toLocaleString('en-AU')} km, ${parts.join(', ')}`;
}

function Tile({
  label,
  value,
  unit,
  meta,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly meta?: string;
  /** Hover text explaining the figure. */
  readonly hint?: string;
}): ReactElement {
  return (
    <li className="metric-tile" title={hint}>
      <span className="label muted">{label}</span>
      <span className="metric metric-tile__value">
        {value}
        <span className="metric-tile__unit body muted"> {unit}</span>
      </span>
      {meta && <span className="caption muted">{meta}</span>}
    </li>
  );
}

const num = (v: number, digits: number): string =>
  v.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });

function Figures({
  data,
  filter,
  rangeText,
  projectName,
  aircraftName,
}: {
  readonly data: OpsRangeRows;
  readonly filter: OpsFilter;
  readonly rangeText: string;
  readonly projectName: (id: string) => string;
  readonly aircraftName: (key: string | null, rego?: string | null) => string;
}): ReactElement {
  const s = opsSummary(data.lines, data.entries, filter);
  const days = kmPerDay(data.lines, filter);
  const rows = aircraftProjectRows(data.lines, data.entries, filter, data.spans);
  const use = utilisationTotal(rows);
  const ds = daysSummary(data.entries, filter);
  const nothing = filterLines(data.lines, filter).length === 0 && rows.length === 0 && ds.items.length === 0;
  const [showTable, setShowTable] = useState(false);
  const standbyMeta = STANDBY_ORDER.map((k) => `${STANDBY_LABEL[k]} ${daysText(s.standby.byType[k])}`).join(
    ' · ',
  );

  return (
    <>
      {nothing && <p className="body">Nothing was flown or recorded in this range.</p>}
      <ul className="metric-tiles" aria-label="Summary">
        <Tile label="Accepted km" value={num(s.acceptedKm, 2)} unit="km" />
        <Tile label="Km flown" value={num(s.flownKm, 2)} unit="km" />
        <Tile
          label="Flight hours"
          value={num(s.flightHours, 1)}
          unit="h"
          meta={`Survey ${hoursText(s.surveyHours)}`}
        />
        <Tile
          label="Survey days"
          hint={SURVEY_DAYS_TILE_HINT}
          value={String(s.surveyDays)}
          unit={s.surveyDays === 1 ? 'day' : 'days'}
          meta={`${s.flights} ${s.flights === 1 ? 'flight' : 'flights'}`}
        />
        <Tile
          label="Standby days"
          value={s.standby.total.toLocaleString('en-AU', { maximumFractionDigits: 2 })}
          unit={s.standby.total === 1 ? 'day' : 'days'}
          meta={standbyMeta}
        />
        <Tile label="Fuel used" value={Math.round(s.fuelUsedL).toLocaleString('en-AU')} unit="L" />
      </ul>

      <section className="panel stack" aria-labelledby="ops-km">
        <h2 id="ops-km" className="heading section-title">
          Km per day
        </h2>
        <p className="caption muted">
          Each line counts on the day it was flown. Km flown includes lines awaiting review; accepted km
          counts reviewed lines only.
        </p>
        <KmPerDayChart days={days} rangeText={rangeText} />
        <div>
          <Button variant="quiet" aria-expanded={showTable} onClick={() => setShowTable((v) => !v)}>
            {showTable ? 'Hide the table' : 'Show the table'}
          </Button>
        </div>
        {showTable && (
          // the chart's figures, days compared down each column: a table on a phone too (rule 5)
          <TableWrap cards={false}>
            <table className="table">
              <caption className="sr-only">Km per day, {rangeText}</caption>
              <thead>
                <tr>
                  <th className="label">Day</th>
                  <th className="label num">Km flown</th>
                  <th className="label num">Accepted km</th>
                  <th className="label num">Flights</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.date}>
                    <th scope="row" className="data">
                      {dateText(d.date)}
                    </th>
                    <td className="num data">{kmText(d.flownKm)}</td>
                    <td className="num data">{kmText(d.acceptedKm)}</td>
                    <td className="num data">{d.flights}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </section>

      <section className="stack" aria-labelledby="ops-aircraft">
        <h2 id="ops-aircraft" className="heading section-title">
          Aircraft and projects
        </h2>
        <p className="caption muted">
          Survey days are the dates with survey hours. Project days are every calendar day from the aircraft’s
          first to its last daily entry on the project, within the range, flown or not; it flew on the days
          with flight hours. The average flight hours per day on the job is flight hours ÷ project days.
          Standby days recorded without an aircraft count for the project only.
        </p>
        {rows.length === 0 ? (
          <p className="body">No flights in this range.</p>
        ) : (
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Aircraft</th>
                  <th className="label">Project</th>
                  <th className="label num">Flights</th>
                  <th className="label num">Flight hours</th>
                  <th className="label num">Survey hours</th>
                  <th className="label num" title={AIRCRAFT_COLUMN_HINT.surveyDays}>
                    Survey days
                  </th>
                  <th className="label num">Km flown</th>
                  <th className="label num">Accepted km</th>
                  <th className="label num">Accepted per survey hour</th>
                  <th className="label num">Fuel used</th>
                  <th className="label num" title={AIRCRAFT_COLUMN_HINT.projectDays}>
                    Project days
                  </th>
                  <th className="label">Utilisation</th>
                  <th className="label num table__head-wrap" title={AIRCRAFT_COLUMN_HINT.hoursPerDay}>
                    Average flight hours per day on the job
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.projectId}|${r.aircraftKey}`}>
                    <th scope="row" className="data">
                      {aircraftName(r.aircraftKey, r.rego)}
                    </th>
                    <td>{projectName(r.projectId)}</td>
                    <td className="num data">{r.flights}</td>
                    <td className="num data">{hoursText(r.flightHours)}</td>
                    <td className="num data">{hoursText(r.surveyHours)}</td>
                    <td className="num data">{r.surveyDays}</td>
                    <td className="num data">{kmText(r.flownKm)}</td>
                    <td className="num data">{kmText(r.acceptedKm)}</td>
                    <td className="num data">{kmPerHourText(r.acceptedKmPerSurveyHour)}</td>
                    <td className="num data">{litresText(r.fuelUsedL)}</td>
                    <td className="num data">{r.projectDays}</td>
                    <td>{utilisationText(r.flyingDays, r.projectDays)}</td>
                    <td className="num data">
                      {r.flightHoursPerAvailableDay === null ? '–' : hoursText(r.flightHoursPerAvailableDay)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 1 && (
                <tfoot>
                  <tr className="progress-table__total">
                    <th scope="row">Total</th>
                    <td />
                    <td className="num data">{s.flights}</td>
                    <td className="num data">{hoursText(s.flightHours)}</td>
                    <td className="num data">{hoursText(s.surveyHours)}</td>
                    <td className="num data">{s.surveyDays}</td>
                    <td className="num data">{kmText(s.flownKm)}</td>
                    <td className="num data">{kmText(s.acceptedKm)}</td>
                    <td className="num data">{kmPerHourText(s.acceptedKmPerSurveyHour)}</td>
                    <td className="num data">{litresText(s.fuelUsedL)}</td>
                    <td className="num data" title={AIRCRAFT_COLUMN_HINT.totalProjectDays}>
                      {use.projectDays}
                    </td>
                    <td>
                      {use.availableDays === use.projectDays
                        ? utilisationText(use.flyingDays, use.projectDays)
                        : utilisationText(use.flyingDays, use.availableDays, 'aircraft')}
                    </td>
                    <td className="num data">
                      {use.flightHoursPerAvailableDay === null
                        ? '–'
                        : hoursText(use.flightHoursPerAvailableDay)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </TableWrap>
        )}
      </section>

      <section className="stack" aria-labelledby="ops-days">
        <h2 id="ops-days" className="heading section-title">
          Standby, maintenance and suspended days
        </h2>
        <p className="caption muted">
          Standby {daysText(ds.standby.total)} ({standbyMeta}), maintenance {daysText(ds.maintenance)},
          suspended {daysText(ds.suspended)}.
        </p>
        {ds.withoutAircraft > 0 && (
          <p className="body">
            {ds.withoutAircraft === 1
              ? '1 entry of this range records no aircraft, so it is left out while an aircraft is chosen.'
              : `${ds.withoutAircraft} entries of this range record no aircraft, so they are left out while an aircraft is chosen.`}
          </p>
        )}
        {ds.items.length === 0 ? (
          <p className="body">No standby, maintenance or suspended days in this range.</p>
        ) : (
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Day</th>
                  <th className="label">Project</th>
                  <th className="label">Aircraft</th>
                  <th className="label">Kind</th>
                  <th className="label num">Days</th>
                  <th className="label">Reason</th>
                </tr>
              </thead>
              <tbody>
                {ds.items.map((i) => (
                  <tr key={i.id}>
                    <th scope="row" className="data">
                      {dateText(i.date)}
                    </th>
                    <td>{projectName(i.projectId)}</td>
                    <td>{aircraftName(i.aircraftId)}</td>
                    <td>
                      {DAY_KIND_LABEL[i.kind]}
                      {i.standbyType ? `: ${STANDBY_LABEL[i.standbyType]}` : ''}
                    </td>
                    <td className="num data">
                      {i.days.toLocaleString('en-AU', { maximumFractionDigits: 2 })}
                    </td>
                    <td>{i.reason || <span className="muted">No comment recorded</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </section>
    </>
  );
}
